import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ActivitiesService } from '@/api';
import type { activities_UpdateActivityRequest } from '@/api';
import { presentTodayKeys } from './usePresentToday';
import { activityKeys } from './useActivities';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react';
import type { ListActivitiesResponse, ActivityResponse } from '@/types/activities';

/**
 * Hook to update an existing activity with optimistic updates.
 *
 * @returns TanStack Query mutation for updating an activity
 *
 * @example
 * const updateActivity = useUpdateActivity();
 * updateActivity.mutate({
 *   id: 'activity-123',
 *   data: { mood: 'neutral' },
 *   comments: [{ text: 'Updated comment' }],
 * });
 */
export function useUpdateActivity() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: activities_UpdateActivityRequest;
    }) => ActivitiesService.putApiV1Activities(id, data),

    onMutate: async ({ id, data }) => {
      // Cancel outgoing refetches to prevent overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: activityKeys.all });

      // Snapshot previous data for rollback
      const previousData = queryClient.getQueriesData({
        queryKey: activityKeys.all,
      });

      // Optimistically update all list queries
      queryClient.setQueriesData<ListActivitiesResponse>(
        { queryKey: activityKeys.lists() },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: (old.items ?? []).map((activity) =>
              activity.id === id
                ? { ...activity, data: data.data, comments: data.comments }
                : activity
            ),
          };
        }
      );

      // Optimistically update detail query
      queryClient.setQueryData(
        activityKeys.detail(id),
        (old: ActivityResponse | undefined) => {
          if (!old) return old;
          return { ...old, data: data.data, comments: data.comments };
        }
      );

      // Return context with snapshot for rollback
      return { previousData };
    },

    onError: (_err, _variables, context) => {
      // Rollback to previous data
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      toast.error(
        i18n._({
          id: 'activities.update.error.title',
          message: 'Failed to update activity',
        })
      );
    },

    onSuccess: () => {
      toast.success(
        i18n._({
          id: 'activities.update.success.title',
          message: 'Activity updated',
        })
      );
    },

    onSettled: () => {
      // Invalidate to refetch and ensure consistency
      queryClient.invalidateQueries({ queryKey: activityKeys.all });
      queryClient.invalidateQueries({ queryKey: presentTodayKeys.all });
    },
  });
}
